import React from 'react'
import { AttachmentWithDetails, downloadAttachment } from '@/services/api/attachment'
import { formatFileSize } from '@/utils/formatters'

interface AttachmentCardProps {
  attachment: AttachmentWithDetails
  onFileClick?: (attachment: AttachmentWithDetails) => void
}

export const AttachmentCard: React.FC<AttachmentCardProps> = ({
  attachment,
  onFileClick
}) => {
  const isImage = attachment.mimeType.startsWith('image/')
  const isVideo = attachment.mimeType.startsWith('video/')
  
  const getFileIcon = () => {
    if (isImage) return '🖼️'
    if (isVideo) return '🎥'
    if (attachment.mimeType.includes('pdf')) return '📄'
    if (attachment.mimeType.includes('word')) return '📝'
    if (attachment.mimeType.includes('excel') || attachment.mimeType.includes('spreadsheet')) return '📊'
    if (attachment.mimeType.includes('zip') || attachment.mimeType.includes('rar')) return '📦'
    return '📁'
  }

  const getFileUrl = () => {
    return `/${attachment.filepath}`
  }

  const handleCardClick = async () => {
    if (onFileClick) {
      onFileClick(attachment)
    } else {
      // 預設行為：下載檔案
      try {
        await downloadAttachment(attachment.id, attachment.originalName)
      } catch (error) {
        console.error('下載失敗:', error)
        alert(`下載失敗: ${error instanceof Error ? error.message : '未知錯誤'}`)
      }
    }
  }

  return (
    <div 
      className="bg-white rounded-lg border border-gray-200 hover:border-gray-300 hover:shadow-md transition-all duration-200 cursor-pointer overflow-hidden"
      onClick={() => handleCardClick()}
    >
      {/* 檔案預覽區域 */}
      <div className="aspect-video bg-gray-50 flex items-center justify-center relative">
        {isImage ? (
          <img
            src={getFileUrl()}
            alt={attachment.originalName}
            className="w-full h-full object-cover"
            onError={(e) => {
              const target = e.target as HTMLImageElement
              target.style.display = 'none'
              const parent = target.parentElement
              if (parent) {
                parent.innerHTML = `<div class="text-6xl">${getFileIcon()}</div>`
              }
            }}
          />
        ) : isVideo ? (
          <video
            src={getFileUrl()}
            className="w-full h-full object-cover"
            muted
            onError={(e) => {
              const target = e.target as HTMLVideoElement
              target.style.display = 'none'
              const parent = target.parentElement
              if (parent) {
                parent.innerHTML = `<div class="text-6xl">${getFileIcon()}</div>`
              }
            }}
          />
        ) : (
          <div className="text-6xl">{getFileIcon()}</div>
        )}
        
        {/* 檔案大小標籤 */}
        <div className="absolute top-2 right-2 bg-black bg-opacity-70 text-white text-xs px-2 py-1 rounded">
          {formatFileSize(attachment.size)}
        </div>
      </div>

      {/* 檔案資訊 */}
      <div className="p-4">
        <h3 className="font-medium text-gray-900 truncate mb-2" title={attachment.originalName}>
          {attachment.originalName}
        </h3>
        
        <div className="space-y-2 text-sm text-gray-600">
          {/* 上傳者 */}
          <div className="flex items-center space-x-2">
            <div className="w-5 h-5 bg-gray-300 rounded-full flex items-center justify-center text-xs">
              {attachment.uploadedBy.avatar ? (
                <img
                  src={attachment.uploadedBy.avatar}
                  alt={attachment.uploadedBy.name}
                  className="w-full h-full rounded-full object-cover"
                />
              ) : (
                attachment.uploadedBy.name.charAt(0).toUpperCase()
              )}
            </div>
            <span className="truncate">{attachment.uploadedBy.name}</span>
          </div>
          
          {/* 關聯議題 */}
          {attachment.issue && (
            <div 
              className="flex items-center space-x-2 cursor-pointer hover:bg-blue-50 rounded px-2 py-1 -mx-2 transition-colors"
              onClick={(e) => {
                e.stopPropagation()
                if (attachment.issue?.id) {
                  window.open(`/issues/${attachment.issue.id}`, '_blank')
                }
              }}
              title={`在新標籤頁中開啟議題: ${attachment.issue?.title || ''}`}
            >
              <span className="text-blue-600">🎫</span>
              <span className="truncate text-blue-600 hover:text-blue-800" title={attachment.issue?.title || ''}>
                {attachment.issue?.key} - {attachment.issue?.title}
              </span>
            </div>
          )}
          
          {/* 上傳時間 */}
          <div className="text-xs text-gray-500">
            {new Date(attachment.createdAt).toLocaleDateString('zh-TW', {
              year: 'numeric',
              month: 'short',
              day: 'numeric',
              hour: '2-digit',
              minute: '2-digit'
            })}
          </div>
        </div>
      </div>
    </div>
  )
}

export default AttachmentCard